import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, orderBy, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { FiCalendar, FiCamera, FiDisc, FiHeart, FiImage } from 'react-icons/fi';
import TopBar from '../components/TopBar';
import MobileNavTabs from '../components/MobileNavTabs';
import DesktopSidebar from '../components/DesktopSidebar';
import desktopLayout from '../components/DesktopSidebarLayout.module.css';
import layoutStyles from '../styles/layout.module.css';
import styles from './Fan.module.css';
import { db, storage } from '../firebase';
import { UserContext } from '../App';
import { createSquareCroppedImage } from '../utils/imageCrop';
import { ANONYMOUS_PHOTO_URL, getUserDisplayName } from '../utils/profileIdentity';
import { createVersionToken, getUserPhotoPath, useStorageUrl, useStorageUrls } from '../utils/storageMedia';
import { getCompactCollectibleImagePath } from '../utils/collectibleMedia';

const PROFILE_IMAGE_SIZE = 1200;
const PROFILE_THUMB_SIZE = 200;

function formatDate(ts) {
  if (!ts) return '—';
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

async function loadPurchasedUnits(uid) {
  const [librarySnap, unitsSnap, paymentsSnap] = await Promise.all([
    getDocs(query(collection(db, 'user_libraries', uid, 'items'), orderBy('purchasedAt', 'desc'))).catch(() => null),
    getDocs(query(collection(db, 'units'), where('ownerUid', '==', uid))).catch(() => null),
    getDocs(query(collection(db, 'users', uid, 'payments'), orderBy('createdAt', 'desc'))).catch(() => null),
  ]);

  const libraryRows = (librarySnap?.docs || []).map((docSnap) => {
    const data = docSnap.data() || {};
    return {
      id: docSnap.id,
      unitId: data.unitId || docSnap.id,
      productId: data.productId || '',
      dropId: data.dropId || '',
      title: data.title || data.productId || docSnap.id,
      media: Array.isArray(data.media) ? data.media : [],
      variantLabel: data.variantLabel || '',
      artistId: data.artistId || '',
      unitNumber: data.unitNumber || null,
      status: data.status || 'owned',
      activeListingId: data.activeListingId || null,
      purchasedAt: data.purchasedAt || data.updatedAt || null,
    };
  });
  if (libraryRows.length) return libraryRows;

  const unitRows = (unitsSnap?.docs || [])
    .map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) }))
    .sort((left, right) => {
      const rightMs = right.purchasedAt?.toMillis?.() || right.createdAt?.toMillis?.() || 0;
      const leftMs = left.purchasedAt?.toMillis?.() || left.createdAt?.toMillis?.() || 0;
      return rightMs - leftMs;
    });
  if (!unitRows.length) return [];

  const productEntries = await Promise.all(unitRows.map(async (unit) => {
    const productId = String(unit.productId || '').trim();
    if (!productId) return [unit.id, {}];
    const productSnap = await getDoc(doc(db, 'products', productId)).catch(() => null);
    return [unit.id, productSnap?.exists() ? { id: productSnap.id, ...(productSnap.data() || {}) } : {}];
  }));
  const productMap = new Map(productEntries);

  const reconstructedFromUnits = unitRows.map((unit) => {
    const product = productMap.get(unit.id) || {};
    return {
      id: unit.id,
      unitId: unit.unitId || unit.id,
      productId: unit.productId || '',
      title: product.title || unit.productId || unit.id,
      media: Array.isArray(product.media) ? product.media : [],
      variantLabel: product.variantLabel || '',
      artistId: product.artistId || '',
      unitNumber: unit.unitNumber || null,
      status: unit.status || 'owned',
      activeListingId: unit.activeListingId || null,
      purchasedAt: unit.purchasedAt || unit.createdAt || null,
    };
  });
  if (reconstructedFromUnits.length) return reconstructedFromUnits;

  const paymentRows = (paymentsSnap?.docs || []).map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) }));
  const orderEntries = await Promise.all(paymentRows.map(async (payment) => {
    const orderId = String(payment.orderId || payment.id || '').trim();
    if (!orderId) return [payment.id, null];
    const orderSnap = await getDoc(doc(db, 'orders', orderId)).catch(() => null);
    return [payment.id, orderSnap?.exists() ? { id: orderSnap.id, ...(orderSnap.data() || {}) } : null];
  }));
  const orderMap = new Map(orderEntries);

  return paymentRows.map((payment) => {
    const order = orderMap.get(payment.id) || {};
    return {
      id: payment.id,
      unitId: String(payment.unitId || order?.unitIds?.[0] || payment.id),
      productId: String(payment.productId || order?.productId || ''),
      dropId: String(payment.dropId || order?.dropId || ''),
      title: String(payment.productTitle || order?.productSnapshot?.title || payment.productId || order?.productId || payment.id),
      media: Array.isArray(payment.media) ? payment.media : (Array.isArray(order?.productSnapshot?.media) ? order.productSnapshot.media : []),
      variantLabel: String(payment.variantLabel || order?.productSnapshot?.variantLabel || ''),
      artistId: String(payment.artistId || order?.productSnapshot?.artistId || ''),
      unitNumber: null,
      status: 'owned',
      activeListingId: null,
      purchasedAt: payment.createdAt || order?.createdAt || null,
    };
  });
}

export default function Fan() {
  const navigate = useNavigate();
  const appUser = useContext(UserContext);
  const inputRef = useRef(null);
  const displayName = useMemo(() => getUserDisplayName(appUser), [appUser]);
  const profilePhotoPath = getUserPhotoPath(appUser, 'thumb');
  const hasProfilePhoto = Boolean(profilePhotoPath);
  const resolvedProfilePhoto = useStorageUrl(profilePhotoPath);
  const profilePhoto = resolvedProfilePhoto || ANONYMOUS_PHOTO_URL;
  const [displayPhoto, setDisplayPhoto] = useState(profilePhoto);
  const [followCount, setFollowCount] = useState(0);
  const [libraryCount, setLibraryCount] = useState(0);
  const [recentUnits, setRecentUnits] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(true);
  const [followLoading, setFollowLoading] = useState(true);
  const [visibleUnitCount, setVisibleUnitCount] = useState(4);
  const [profileFile, setProfileFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [savingPhoto, setSavingPhoto] = useState(false);
  const [photoStatus, setPhotoStatus] = useState('');
  const [photoError, setPhotoError] = useState('');

  useEffect(() => {
    if (!appUser?.id) {
      setFollowCount(0);
      setFollowLoading(false);
      return undefined;
    }
    let active = true;
    (async () => {
      try {
        setFollowLoading(true);
        const followsSnap = await getDocs(collection(db, 'users', appUser.id, 'follows'));
        if (active) setFollowCount(followsSnap.size);
      } catch {
        if (active) setFollowCount(0);
      } finally {
        if (active) setFollowLoading(false);
      }
    })();
    return () => { active = false; };
  }, [appUser?.id]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  useEffect(() => {
    setDisplayPhoto(profilePhoto);
  }, [profilePhoto]);

  const recentUnitPaths = useMemo(
    () => recentUnits.map((item) => getCompactCollectibleImagePath(item)).filter(Boolean),
    [recentUnits]
  );
  const recentUnitUrls = useStorageUrls(recentUnitPaths);
  const visibleUnits = useMemo(() => recentUnits.slice(0, visibleUnitCount), [recentUnits, visibleUnitCount]);
  const hasMoreUnits = visibleUnitCount < recentUnits.length;

  const handleFileChange = (event) => {
    setPhotoError('');
    setPhotoStatus('');
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setPhotoError('Choose an image file.');
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    const objectUrl = URL.createObjectURL(file);
    setProfileFile(file);
    setPreviewUrl(objectUrl);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const handleSavePhoto = async () => {
    if (!appUser?.id) return;
    if (!profileFile || !croppedAreaPixels) {
      setPhotoError(profileFile ? 'Adjust the crop before saving.' : 'Choose a photo first.');
      return;
    }

    setSavingPhoto(true);
    setPhotoError('');
    setPhotoStatus('');

    try {
      const [largeFile, thumbFile] = await Promise.all([
        createSquareCroppedImage({ file: profileFile, crop: croppedAreaPixels, size: PROFILE_IMAGE_SIZE }),
        createSquareCroppedImage({ file: profileFile, crop: croppedAreaPixels, size: PROFILE_THUMB_SIZE }),
      ]);

      const ext = largeFile.name.includes('.') ? largeFile.name.split('.').pop() : 'jpg';
      const versionToken = createVersionToken();
      const photoPath = `users/${appUser.id}/profile-1200-v${versionToken}.${ext}`;
      const photoThumbPath = `users/${appUser.id}/profile-200-v${versionToken}.${ext}`;
      const largeRef = ref(storage, photoPath);
      const thumbRef = ref(storage, photoThumbPath);

      await Promise.all([
        uploadBytes(largeRef, largeFile, { contentType: largeFile.type || 'image/jpeg' }),
        uploadBytes(thumbRef, thumbFile, { contentType: thumbFile.type || 'image/jpeg' }),
      ]);

      await setDoc(doc(db, 'users', appUser.id), {
        photoPath,
        photoThumbPath,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      setPhotoStatus('Fan photo updated.');
      setDisplayPhoto(previewUrl || profilePhoto);
      setProfileFile(null);
      setPreviewUrl('');
      if (inputRef.current) inputRef.current.value = '';
    } catch (err) {
      setPhotoError(err?.message || 'Unable to update fan photo.');
    } finally {
      setSavingPhoto(false);
    }
  };

  useEffect(() => {
    if (!appUser?.id) {
      setLibraryCount(0);
      setRecentUnits([]);
      setVisibleUnitCount(4);
      setLibraryLoading(false);
      return undefined;
    }
    let active = true;
    (async () => {
      try {
        setLibraryLoading(true);
        const rows = await loadPurchasedUnits(appUser.id);
        if (!active) return;
        setRecentUnits(rows);
        setVisibleUnitCount(4);
        setLibraryCount(rows.length);
      } catch {
        if (!active) return;
        setLibraryCount(0);
        setRecentUnits([]);
      } finally {
        if (active) setLibraryLoading(false);
      }
    })();
    return () => { active = false; };
  }, [appUser?.id]);

  return (
    <div className={layoutStyles.homeContainer} style={{ paddingBottom: 0 }}>
      <TopBar hideLeft>
        <div className={desktopLayout.mobileTabs}><MobileNavTabs /></div>
      </TopBar>
      <div className={`${desktopLayout.shell} ${desktopLayout.shellWithRail}`.trim()}>
        <DesktopSidebar active="fan" note="Fan identity and recent product-unit ownership live here." />
        <main className={desktopLayout.main}>
          <div className={desktopLayout.mainInner}>
            <div className={styles.pageShell}>
              <section className={styles.heroCard}>
                <div className={styles.identityCard}>
                  <div className={styles.identityMediaWrap}>
                    <img className={`${styles.photo} ${!hasProfilePhoto && !previewUrl ? styles.photoFallback : ''}`.trim()} src={previewUrl || displayPhoto} alt={displayName} />
                  </div>
                  <div className={styles.identityContent}>
                    <span className={styles.eyebrow}>Fan</span>
                    <div className={styles.identityHeading}>
                      <h1>{displayName}</h1>
                      <span className={styles.identityBadge}><FiHeart /> Collector</span>
                    </div>
                    <p>{hasProfilePhoto || previewUrl ? 'Your fan identity is live and ready across the app.' : 'Add a profile photo so your fan identity feels complete everywhere you appear.'}</p>
                    <div className={styles.identityActions}>
                      <button type="button" className={hasProfilePhoto && !previewUrl ? styles.inlineButton : styles.primaryButton} onClick={() => inputRef.current?.click()}>
                        <FiCamera />
                        {hasProfilePhoto && !previewUrl ? 'Change Photo' : 'Upload Photo'}
                      </button>
                      {profileFile ? (
                        <button type="button" className={styles.primaryButton} onClick={handleSavePhoto} disabled={savingPhoto || !profileFile}>
                          {savingPhoto ? 'Saving…' : 'Save Photo'}
                        </button>
                      ) : null}
                    </div>
                    <input ref={inputRef} type="file" accept="image/*" onChange={handleFileChange} className={styles.hiddenInput} />
                    {photoStatus ? <p className={styles.successState}>{photoStatus}</p> : null}
                    {photoError ? <p className={styles.errorState}>{photoError}</p> : null}
                  </div>
                </div>
                {previewUrl ? (
                  <div className={styles.cropSection}>
                    <div className={styles.cropperFrame}>
                      <Cropper
                        image={previewUrl}
                        crop={crop}
                        zoom={zoom}
                        aspect={1}
                        cropShape="round"
                        showGrid={false}
                        cropSize={{ width: 220, height: 220 }}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
                      />
                    </div>
                    <label className={styles.sliderLabel} htmlFor="fanPhotoZoom">Zoom</label>
                    <input id="fanPhotoZoom" type="range" min="1" max="3" step="0.01" value={zoom} onChange={(event) => setZoom(Number(event.target.value))} />
                  </div>
                ) : null}
              </section>

              <section className={styles.summaryGrid}>
                <button type="button" className={`${styles.statCard} ${styles.statButton}`.trim()} onClick={() => navigate('/fan/following')}>
                  <span className={styles.statLabel}><FiHeart /> Artists Following</span>
                  <strong>{followLoading ? '…' : followCount}</strong>
                  <p>Open the artists you already follow.</p>
                </button>
                <div className={styles.statCard}><span className={styles.statLabel}><FiDisc /> Collectibles Owned</span><strong>{libraryLoading ? '…' : libraryCount}</strong><p>See every collectible that now lives in your library.</p></div>
              </section>

              <section className={`${styles.card} ${styles.collectiblesCard}`.trim()}>
                <div className={styles.sectionHeader}>
                  <div>
                    <h2><FiImage /> Purchased Collectibles</h2>
                    <p className={styles.sectionLead}>Your latest purchased collectibles, ready to reopen anytime.</p>
                  </div>
                </div>
                {libraryLoading ? <p className={styles.emptyState}>Loading library…</p> : null}
                {!libraryLoading && visibleUnits.length ? (
                  <ul className={styles.collectibleGrid}>
                    {visibleUnits.map((item) => (
                      <li key={item.unitId || item.id} className={styles.assetItem}>
                        <button type="button" className={`${styles.assetButton} ${styles.collectibleButton}`.trim()} onClick={() => navigate(`/units/${item.unitId || item.id}`, { state: { backTo: '/fan' } })}>
                          <div className={`${styles.assetMedia} ${styles.collectibleMedia}`.trim()}>
                            {recentUnitUrls[getCompactCollectibleImagePath(item)] ? <img src={recentUnitUrls[getCompactCollectibleImagePath(item)]} alt={item.title || 'Unit'} /> : <div className={styles.assetPlaceholder}>Unit</div>}
                          </div>
                          <div className={styles.assetBody}>
                            <strong>{item.title || item.unitId || item.id}</strong>
                            <div className={styles.assetMeta}>
                              <span><FiDisc /> Unit #{item.unitNumber || '—'}</span>
                              <span>{item.activeListingId ? 'Listed for sale' : 'Owned'}</span>
                            </div>
                            <div className={styles.assetMeta}>
                              <span><FiCalendar /> {formatDate(item.purchasedAt)}</span>
                            </div>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {!libraryLoading && hasMoreUnits ? (
                  <div className={styles.loadMoreRow}>
                    <button type="button" className={styles.loadMoreButton} onClick={() => setVisibleUnitCount((count) => count + 4)}>
                      Load more
                    </button>
                  </div>
                ) : null}
                {!libraryLoading && !recentUnits.length ? (
                  <div className={styles.emptyLibraryState}>
                    <span className={styles.emptyLibraryBadge}>Start your collection</span>
                    <h3>Your fan profile is waiting for its first collectible.</h3>
                    <p>Follow artists, watch the countdowns, and claim a live drop so your purchased collectibles start showing up here.</p>
                    <button type="button" className={styles.primaryButton} onClick={() => navigate('/home')}>Browse Live Artists</button>
                  </div>
                ) : null}
              </section>
            </div>
          </div>
        </main>
        <aside className={desktopLayout.rightRail}>
          <div className={desktopLayout.railCard}>
            <span className={desktopLayout.railEyebrow}>Fan</span>
            <h3 className={desktopLayout.railTitle}>Collection snapshot</h3>
            <p className={desktopLayout.railCopy}>See who you follow and jump straight into the collectible units you already own.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
